import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Group, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import type { ComposeRecipientResult } from '@repletoryreach/sdk-api/inbox';
import { useT, type Translate } from '../../../../i18n/I18nProvider';

export interface SendResultsProps {
  results: ComposeRecipientResult[];
  sending: boolean;
  /** Send again, to the people it did not reach. */
  onRetry: (msisdns: string[]) => void;
  /** Start over with an empty message. */
  onNew: () => void;
  /** Who Meta bills for the sending number: on the business's own account a billing refusal is theirs to fix. */
  metaPayer?: 'platform' | 'customer';
}

/**
 * What became of every person, after the send.
 *
 * A partial failure is the case this whole panel exists for. Sending to five
 * people and reaching four is not a success and not a failure; it is four
 * conversations and one problem, and both halves have to be visible. So every
 * recipient gets a row saying which they were, a failure says why in words,
 * and the failures alone can be tried again without retyping the message or
 * re-charging the four that went.
 */
export function SendResults({ results, sending, onRetry, onNew, metaPayer }: SendResultsProps) {
  const t = useT();
  const nav = useNavigate();

  const sent = results.filter((r) => r.status === 'sent');
  const failed = results.filter((r) => r.status !== 'sent');
  // A refusal nothing can be done about is not offered a retry button that
  // will refuse identically.
  const retryable = failed.filter((r) => r.error?.code !== 'OPTED_OUT');

  return (
    <Card withBorder className="rr-compose__results">
      <Stack gap="md">
        {failed.length === 0 ? (
          <Alert color="success" title={t('compose.results.allSent.title')}>
            {t('compose.results.allSent.body', { count: sent.length })}
          </Alert>
        ) : sent.length === 0 ? (
          <Alert color="error" role="alert" title={t('compose.results.noneSent.title')}>
            {t('compose.results.noneSent.body', { count: failed.length })}
          </Alert>
        ) : (
          <Alert color="warning" title={t('compose.results.partial.title')}>
            {t('compose.results.partial.body', { sent: sent.length, failed: failed.length })}
          </Alert>
        )}

        <Stack gap={2} className="rr-compose__states">
          {results.map((r) => (
            <div key={r.msisdn} className="rr-compose__state">
              <div className="rr-compose__state-who">
                <Text role="bodySmall" fw={600}>{r.contact_name ?? r.msisdn}</Text>
                <Text role="caption" c="var(--rr-color-text-muted)">
                  {r.status === 'sent'
                    ? (r.contact_name ? r.msisdn : t('compose.results.delivered'))
                    : reason(r, t, metaPayer)}
                </Text>
              </div>
              <Group gap="xs" align="center" wrap="nowrap">
                {r.status === 'sent' && r.conversation_id && (
                  <Button
                    size="xs"
                    variant="subtle"
                    onClick={() => nav(`/inbox?conversation=${encodeURIComponent(r.conversation_id!)}`)}
                  >
                    {t('compose.results.open')}
                  </Button>
                )}
                <Badge size="xs" color={r.status === 'sent' ? 'green' : 'red'} variant="light">
                  {t(r.status === 'sent' ? 'compose.results.badge.sent' : 'compose.results.badge.failed')}
                </Badge>
              </Group>
            </div>
          ))}
        </Stack>

        <Group gap="xs" justify="flex-end">
          <Button variant="default" size="sm" onClick={() => nav('/inbox')}>
            {t('compose.results.backToInbox')}
          </Button>
          <Button variant="default" size="sm" onClick={onNew}>
            {t('compose.results.another')}
          </Button>
          {retryable.length > 0 && (
            <Button
              size="sm"
              loading={sending}
              onClick={() => onRetry(retryable.map((r) => r.msisdn))}
            >
              {t('compose.results.retry', { count: retryable.length })}
            </Button>
          )}
        </Group>
      </Stack>
    </Card>
  );
}

/**
 * Why one person was not reached, in their own words where the server gave
 * some. The codes are translated so the sentence is in the reader's language;
 * an unrecognised one falls back to the server's own message rather than to
 * "something went wrong".
 */
function reason(r: ComposeRecipientResult, t: Translate, metaPayer?: 'platform' | 'customer'): string {
  const code = r.error?.code;
  if (!code) return t('compose.results.reason.unknown');
  if (code === 'ACCOUNT_NOT_PAYABLE' && metaPayer === 'customer') return t('compose.results.reason.ACCOUNT_NOT_PAYABLE.customer');
  const translated = t(`compose.results.reason.${code}`, '');
  return translated || r.error?.message || t('compose.results.reason.unknown');
}
